import { TableColumn } from "@models/components/tableColumn";
import { SignatoryRegime } from "@models/clients/clients.details/signatoryRegime";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";

export class ClientDetailsRecipientsController {
    public client: any;
    public clientNumber: string; // Add clientNumber binding
    public isViewOnly: boolean = false;
    public signatoryRegimes: any[] = [];
    public isLoadingSignatoryRegime: boolean = true;
    
    // Dropdown options
    public dropdownOptions: any = {};
    public clientPortfolioAccounts: any[] = [];
    
    public signatoryRegimeColumns: TableColumn[] = [
        { key: 'portfolioAccount', label: 'Portfolio Number', type: 'text' },
        { key: 'signatoryRegimeTypeName', label: 'Signatory Regime', type: 'text' },
        { key: 'notesSection', label: 'Notes Section', type: 'text' },
        { key: 'bankName', label: 'Bank Name', type: 'text' },
        { key: 'custodyAccountNumberDebit', label: 'Custody Account Number', type: 'text' },
        { key: 'currencyName', label: 'Currency', type: 'text' }
    ];

    /** @ngInject */
    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private clientCRMRepo: ClientCRMRepo,
        private dropdownOptionsService: DropdownOptionsService
    ) {}

    public $onInit(): void {
        // Component initialization logic for signatory regimes
        // Use clientNumber for initial data loading instead of waiting for client
        if (this.clientNumber && this.clientNumber !== 'Individual' && this.clientNumber !== 'Institution') {
            this.loadSignatoryRegimes();
            this.loadClientPortfolioAccounts();
        }
        
        // Load dropdown options
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.loadDropdownOptions(options);
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options:', error);
            });
    }


    public loadSignatoryRegimes(): void {
        this.isLoadingSignatoryRegime = true;
        const clientNumber = this.clientNumber
        
        if (clientNumber) {
            this.clientCRMRepo.getSignatoryRegimes(clientNumber)
                .then((response: any) => {
                    this.signatoryRegimes = response.data || [];
                    this.isLoadingSignatoryRegime = false;
                })
                .catch((error) => {
                    console.error('Error loading signatory regimes:', error);
                    this.isLoadingSignatoryRegime = false;
                });
        }
    }

    public loadClientPortfolioAccounts(): void {
        // Use clientNumber if available, otherwise fall back to client.id
        const clientNumber = this.clientNumber
        
        if (clientNumber) {
            this.clientCRMRepo.getClientPortfolioAccounts(clientNumber)
                .then((response: any) => {
                    this.clientPortfolioAccounts = response.data || [];
                })
                .catch((error) => {
                    console.error('Error loading client portfolio accounts:', error);
                });
        }
    }



    public newSignatoryRegime(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.signatoryRegime = {
            portfolioNumber: '',
            signatoryRegimeType: undefined,
            bank: undefined,
            currency: undefined,
            custodyAccountNumberDebit: '',
            notesSection: '',
            linkToDocument: ''
        } as Partial<SignatoryRegime>;
        
        // Pass dropdown options and portfolio accounts to the modal
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-signatory-regime-modal client="client" signatory-regime="signatoryRegime" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-signatory-regime-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                console.log('result', result);
                if (result) {
                    this.signatoryRegimeSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening signatory regime modal:', error);
            });
    }

    public editSignatoryRegime(signatoryRegime: any): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.signatoryRegime = signatoryRegime;
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-signatory-regime-modal client="client" signatory-regime="signatoryRegime" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-signatory-regime-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.signatoryRegimeSaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening signatory regime modal:', error);
            });
    }

    public deleteSelectedSignatoryRegimes(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Signatory Regimes')
            .textContent(`Are you sure you want to delete ${itemIds.length} signatory regime(s)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.isLoadingSignatoryRegime = true;
            return this.clientCRMRepo.deleteSignatoryRegimes(itemIds)
                .then(() => {
                    this.loadSignatoryRegimes();
                })
                .catch((error) => {
                    console.error('Error deleting signatory regimes:', error);
                    this.isLoadingSignatoryRegime = false;
                    throw error;
                });
        });
    }

    private signatoryRegimeSaved(result: any): void {
        if (result && this.client && this.client.id) {
            this.isLoadingSignatoryRegime = true;
            this.clientCRMRepo.saveSignatoryRegime(this.client.id, result)
                .then((response: any) => {
                    // Reload the signatory regimes from the backend
                    this.loadSignatoryRegimes();
                    this.isLoadingSignatoryRegime = false;
                })
                .catch((error) => {
                    console.error('Error saving signatory regime:', error);
                    this.isLoadingSignatoryRegime = false;
                });
        }
    }

    private loadDropdownOptions(options: any): void {
        // Store all options in the single dropdownOptions object
        this.dropdownOptions = options;
    }
}

export const ClientDetailsRecipientsComponent: angular.IComponentOptions = {
    template: require('./clientdetails.recipients.html'),
    controller: ClientDetailsRecipientsController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        clientNumber: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/recipients/clientdetails.recipients.ts